import React, {useCallback, useMemo, useRef} from 'react';
import {
    Box,
    Button,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {alpha, SxProps, Theme} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {B7_PAGE, B7_SEED_ROWS} from '@/pages/components/audit-program/b-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface B7UnadjustedMisstatementsViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const ROW_FIELDS = [
    'narrative',
    'extDr',
    'extCr',
    'actDr',
    'actCr',
    'pl',
    'bs',
] as const;

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.7rem',
    py: 0.75,
    px: 0.4,
    lineHeight: 1.25,
    whiteSpace: 'pre-line' as const,
};
const bodySx = {
    ...cellBorder,
    py: 0.4,
    px: 0.4,
    verticalAlign: 'top' as const,
    overflow: 'hidden',
};

const nativeBaseSx: SxProps<Theme> = {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    m: 0,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    px: 0.75,
    py: '4px',
    fontSize: (theme) => theme.typography.body2.fontSize,
    lineHeight: 1.25,
    fontFamily: 'inherit',
    color: 'text.primary',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    outline: 'none',
    '&:hover': {borderColor: 'text.primary'},
    '&:focus': {
        borderColor: 'primary.main',
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
    },
};

function CellInput({
    value,
    onChange,
    multiline,
    align = 'left',
    rows = 2,
}: {
    value: string;
    onChange: (v: string) => void;
    multiline?: boolean;
    align?: 'left' | 'center' | 'right';
    rows?: number;
}) {
    if (multiline) {
        return (
            <Box
                component="textarea"
                value={value}
                rows={rows}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
                sx={{...nativeBaseSx, resize: 'vertical', textAlign: align}}
            />
        );
    }
    return (
        <Box
            component="input"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={{...nativeBaseSx, textAlign: align}}
        />
    );
}

function parseRowIds(raw?: string): string[] {
    if (!raw || !String(raw).trim()) return [];
    return String(raw)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
}

function discoverLegacyRowIds(values: Record<string, string>): string[] {
    const ids = new Set<string>();
    for (const seed of B7_SEED_ROWS) ids.add(seed.id);
    for (const key of Object.keys(values)) {
        const m = /^(r\d+|row_\d+)\.(narrative|extDr|extCr|actDr|actCr|pl|bs)$/.exec(key);
        if (m) ids.add(m[1]);
    }
    // migrate old scheduleNote into first seed row if present and no narrative yet
    if (values.scheduleNote && !values['r11.narrative'] && !values.rowIds) {
        ids.add('r11');
    }
    const seedIds: string[] = B7_SEED_ROWS.map((r) => r.id);
    const rest = [...ids].filter((id) => !seedIds.includes(id)).sort();
    const ordered = [...seedIds.filter((id) => ids.has(id)), ...rest];
    return ordered.length ? ordered : seedIds.slice();
}

function nextRowId(existing: string[]): string {
    let n = 1;
    const set = new Set(existing);
    while (set.has(`row_${n}`)) n += 1;
    return `row_${n}`;
}

/**
 * Excel B7 — Unadjusted Misstatements.
 * Free-form schedule table (add/delete) + static conclusion statements (no Comments/WP Ref).
 */
export default function B7UnadjustedMisstatementsView({
    header,
    values,
    onChange,
}: B7UnadjustedMisstatementsViewProps) {
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const set = useCallback((key: string, value: string) => {
        onChangeRef.current(key, value);
    }, []);

    const rowIds = useMemo(() => {
        const explicit = parseRowIds(values.rowIds);
        if (explicit.length) return explicit;
        return discoverLegacyRowIds(values);
    }, [values]);

    const f = (id: string, field: (typeof ROW_FIELDS)[number]) => {
        const direct = values[`${id}.${field}`];
        if (direct != null && String(direct).trim() !== '') return direct;
        // one-time migrate old free-text scheduleNote into first row narrative
        if (field === 'narrative' && id === 'r11' && values.scheduleNote) {
            return values.scheduleNote;
        }
        return '';
    };

    const handleAdd = () => {
        const id = nextRowId(rowIds);
        set('rowIds', [...rowIds, id].join(','));
        for (const field of ROW_FIELDS) set(`${id}.${field}`, '');
    };

    const handleDelete = (id: string) => {
        if (rowIds.length <= 1) return;
        set('rowIds', rowIds.filter((x) => x !== id).join(','));
        for (const field of ROW_FIELDS) set(`${id}.${field}`, '');
    };

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a13"
                refCode="B7"
                values={values}
                onChange={set}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 1.5, mt: 0.5}}
            >
                {B7_PAGE.title}
            </Typography>

            <Stack spacing={1.5} sx={{mb: 2}}>
                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems="center">
                    <Typography variant="body2" sx={{minWidth: 140}}>
                        {B7_PAGE.finalMaterialityLabel}
                    </Typography>
                    <Box sx={{flex: 1, maxWidth: 240}}>
                        <CellInput
                            value={values.finalMateriality ?? ''}
                            onChange={(v) => set('finalMateriality', v)}
                        />
                    </Box>
                </Stack>
                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems="center">
                    <Typography variant="body2" sx={{minWidth: 220}}>
                        {B7_PAGE.trivialLabel}
                    </Typography>
                    <Box sx={{flex: 1, maxWidth: 240}}>
                        <CellInput
                            value={values.trivialThreshold ?? ''}
                            onChange={(v) => set('trivialThreshold', v)}
                        />
                    </Box>
                </Stack>
            </Stack>

            <Typography variant="body2" fontWeight={700} sx={{mb: 1}}>
                Schedule of unadjusted misstatements
            </Typography>

            <TableContainer sx={{overflowX: 'auto', mb: 1}}>
                <Table size="small" sx={{minWidth: 1100, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col style={{width: '22%'}}/>
                        <col style={{width: '10%'}}/>
                        <col style={{width: '10%'}}/>
                        <col style={{width: '10%'}}/>
                        <col style={{width: '10%'}}/>
                        <col style={{width: '14%'}}/>
                        <col style={{width: '14%'}}/>
                        <col style={{width: '5%'}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} rowSpan={2}>Narrative</TableCell>
                            <TableCell sx={headSx} align="center" colSpan={2}>
                                Extrapolated misstatements
                            </TableCell>
                            <TableCell sx={headSx} align="center" colSpan={2}>
                                Actual misstatements
                            </TableCell>
                            <TableCell sx={headSx} align="center" rowSpan={2}>
                                Profit and loss
                            </TableCell>
                            <TableCell sx={headSx} align="center" rowSpan={2}>
                                Balance sheet
                            </TableCell>
                            <TableCell sx={headSx} rowSpan={2}/>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={headSx} align="center">DR</TableCell>
                            <TableCell sx={headSx} align="center">CR</TableCell>
                            <TableCell sx={headSx} align="center">DR</TableCell>
                            <TableCell sx={headSx} align="center">CR</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rowIds.map((id) => (
                            <TableRow key={id}>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(id, 'narrative')}
                                        onChange={(v) => set(`${id}.narrative`, v)}
                                        multiline
                                        rows={2}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput value={f(id, 'extDr')} onChange={(v) => set(`${id}.extDr`, v)} align="right"/>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput value={f(id, 'extCr')} onChange={(v) => set(`${id}.extCr`, v)} align="right"/>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput value={f(id, 'actDr')} onChange={(v) => set(`${id}.actDr`, v)} align="right"/>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput value={f(id, 'actCr')} onChange={(v) => set(`${id}.actCr`, v)} align="right"/>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput value={f(id, 'pl')} onChange={(v) => set(`${id}.pl`, v)} align="right"/>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput value={f(id, 'bs')} onChange={(v) => set(`${id}.bs`, v)} align="right"/>
                                </TableCell>
                                <TableCell sx={{...bodySx, px: 0}} align="center">
                                    <IconButton
                                        size="small"
                                        aria-label="delete row"
                                        onClick={() => handleDelete(id)}
                                        disabled={rowIds.length <= 1}
                                        sx={{color: 'text.secondary'}}
                                    >
                                        <DeleteOutlineIcon fontSize="small"/>
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell sx={{...bodySx, fontWeight: 700}}>
                                Total of potential adjustments
                            </TableCell>
                            {(['totalExtDr', 'totalExtCr', 'totalActDr', 'totalActCr', 'totalPl', 'totalBs'] as const).map((k) => (
                                <TableCell key={k} sx={bodySx}>
                                    <CellInput
                                        value={values[k] ?? ''}
                                        onChange={(v) => set(k, v)}
                                        align="right"
                                    />
                                </TableCell>
                            ))}
                            <TableCell sx={bodySx}/>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2.5}}>
                <Button size="small" startIcon={<AddIcon/>} onClick={handleAdd} variant="outlined">
                    Add row
                </Button>
            </Box>

            <Typography variant="body2" fontWeight={700} sx={{mb: 1}}>
                {B7_PAGE.conclusionTitle}
            </Typography>
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                }}
            >
                {B7_PAGE.conclusionItems.map((item) => (
                    <Typography
                        key={item.id}
                        variant="body2"
                        sx={{mb: 1, lineHeight: 1.45, whiteSpace: 'pre-line'}}
                    >
                        <Box component="span" fontWeight={600} sx={{mr: 0.75}}>
                            {item.num}.
                        </Box>
                        {item.text}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
}
